import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { HeaderSubLabel, SubColumnDef, subColumnField } from '@components/DataGrid/subColumns';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import RenderCell from '@components/DataGrid/RenderCell';
import DateSlot from '@components/DataGrid/DateSlot';
import { GridColDefExtended } from '@mui/x-data-grid-pro';
import { defaultSortComparator } from '@components/DataGrid/types';

import styles from '../../RenderCell.module.css';

const vesselField = 'Vessel';

/** Sub-values of the Vessel column sortable via hidden companion columns */
const vesselSubColumns: SubColumnDef<DispatchLeg>[] = [{ key: 'voyage', label: 'Voyage', valueGetter: (row) => row.voyage }];

const vesselSubLabels: HeaderSubLabel[] = vesselSubColumns.map((sub) => ({
	label: sub.label,
	field: subColumnField(vesselField, sub.key)
}));

const vesselDefinition: GridColDefExtended<DispatchLeg> = {
	field: vesselField,
	minWidth: 125,
	width: 125,
	headerName: 'Vessel|||Voyage',
	subColumns: vesselSubColumns,
	/** Mobile cards invoke renderHeader without params (RenderCardCell), so they fall back to the
	 * static secondaryText instead of mounting the grid-subscribing sort labels per card */
	renderHeader: (params) => (
		<RenderHeader
			primaryText='Vessel'
			subLabels={params.field ? vesselSubLabels : undefined}
			secondaryText='Voyage'
		/>
	),
	renderCell: ({ row }) => (
		<RenderCell
			primarySlot={
				row.vslName && (
					<span
						className={styles.bold}
						title={row.vslName}
					>
						{row.vslName}
					</span>
				)
			}
			secondarySlot={
				row.voyage && (
					<span
						className={styles['secondary-text']}
						title={row.voyage}
					>
						{row.voyage}
					</span>
				)
			}
			tertiarySlot={
				<DateSlot
					toDate={row.voyageEtaParseDate}
					dateOnly
				/>
			}
		/>
	),
	disableReorder: true,
	getApplyQuickFilterFn: (value: string) => {
		return (_, row): boolean =>
			applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
				const { vslName, voyage } = row;
				return caseInsensitiveIncludes(value, [vslName, voyage]);
			});
	},
	valueGetter: (_, row: DispatchLeg) => `${row.vslName ?? ''}|||${row.voyage ?? ''}`,
	sortable: true,
	sortComparator: (v1, v2, cellParams1) => {
		if (typeof v1 === 'string' && typeof v2 === 'string') {
			const [v1Vessel, v1Voyage] = v1.split('|||');
			const [v2Vessel, v2Voyage] = v2.split('|||');

			return (
				defaultSortComparator(v1Vessel, v2Vessel, cellParams1) ||
				defaultSortComparator(v1Voyage, v2Voyage, cellParams1)
			);
		}

		return 0;
	}
};

export default vesselDefinition;
